import { Injectable } from '@angular/core';
import {
    AbstractControl,
    FormControl,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
} from '@angular/forms';
import { Subject, takeUntil, tap } from 'rxjs';
import { nameOf } from '../../../../../../../core/functions/string-helpers';
import { FormGroupType } from '../../../../../../../core/models/form-group-type';
import { CardOnFileValidationMessages } from '../constants/card-on-file-validation-messages';
import { CardExpirationBaseFormModel } from '../models/card-expiration-base-form-model';
import { DefaultCardSelectionBaseFormModel } from '../models/default-card-selection-base-form-model';

@Injectable({
    providedIn: 'root',
})
export class CardOnFileFormService {
    /**
     * Sets up a listener on the expiration year control to trigger validation of the expiration month whenever the year changes.
     * This ensures that if the user selects the current year, the month validator will check that the month is not in the past.
     */
    setupExpirationYearListener<T extends CardExpirationBaseFormModel>(
        form: FormGroup<FormGroupType<T>>,
        destroy$: Subject<void>,
    ): void {
        const expirationYearControl = form.controls
            .expirationYear as FormControl;

        expirationYearControl.valueChanges
            .pipe(
                takeUntil(destroy$),
                tap(() => {
                    form.controls.expirationMonth.updateValueAndValidity();
                }),
            )
            .subscribe();
    }

    /**
     * Validates the expiration month against the selected expiration year. If the selected year
     * is the current year, the month must not be in the past.
     */
    expirationMonthValidator<
        T extends CardExpirationBaseFormModel,
    >(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const month = control.value as string | null;
            if (!month) {
                return null;
            }

            const form = control.parent as FormGroup<FormGroupType<T>>;
            if (!form) {
                return null;
            }

            const year = form.controls.expirationYear.value;
            if (!year) {
                return null;
            }

            const now = new Date();
            const currentYear = now.getFullYear();
            const currentMonth = now.getMonth() + 1;
            const selectedYear = parseInt(year, 10);
            const selectedMonth = parseInt(month, 10);

            if (selectedYear === currentYear && selectedMonth < currentMonth) {
                return {
                    [nameOf<typeof CardOnFileValidationMessages>(
                        'expirationMonthExpired',
                    )]: CardOnFileValidationMessages.expirationMonthExpired,
                };
            }

            return null;
        };
    }

    /**
     * Mark the card as the default and disable the option to change it. This is used when there
     * is only one card on file that will be created or edited, as that card must be the default
     * and the user should not have the option to deselect it as the default.
     *
     * @param form The form group for the card on file form, which must include an is default control.
     */
    setCardAsDefault<T extends DefaultCardSelectionBaseFormModel>(
        form: FormGroup<FormGroupType<T>>,
    ): void {
        form.controls.isDefault.patchValue(true, { emitEvent: false });

        // Disabling the control to prevent the user from changing the default selection when there
        // is one card on file being added or edited, as that card must be the default.
        form.controls.isDefault.disable({ emitEvent: false });
    }
}
